/**
 * Quote Search Utilities
 */

import { generateSearchQuery } from "@/utils/search";
/**
 * Status Actions Utility
 *
 * Controls which actions are available based on the current quote status
 */

import { QuoteStatus } from "../types";

export interface AvailableActions {
  canMarkAsRevised: boolean;
  canConvertToOrder: boolean;
  canMarkAsExpired: boolean;
  canMarkAsFollowUp: boolean;
}

/**
 * Get available actions based on current quote status
 */
export const getAvailableActions = (statuscode: number): AvailableActions => {
  const actions: AvailableActions = {
        canMarkAsRevised: false,
    canConvertToOrder: false,
    canMarkAsExpired: false,
    canMarkAsFollowUp: false,
  };

  switch (statuscode) {
    case QuoteStatus.FOLLOW_UP_NEEDED: // 1 - In Progress
      actions.canMarkAsRevised = false;
      actions.canConvertToOrder = false;
      actions.canMarkAsExpired = false;
      actions.canMarkAsFollowUp = false;
      break;

    case QuoteStatus.LIVE: // 2 - Live
      actions.canMarkAsFollowUp = true;
      actions.canConvertToOrder = true;
      actions.canMarkAsExpired = true;
      actions.canMarkAsRevised = true;

      break;

    case QuoteStatus.CONVERTED: // 4 - Converted (Won)
      // Can mark as in progress if needed
      break;

    case QuoteStatus.LOST: // 5 - Lost
      // Can reopen as Live or In Progress
      break;

    case QuoteStatus.EXPIRED: // 6 - Expired
      // Can reopen as Live or In Progress
      actions.canMarkAsRevised = false;
      break;

    case QuoteStatus.REVISED: // 7 - Revised (Project)
      actions.canConvertToOrder = false;
      actions.canMarkAsExpired = false;
      break;

    case QuoteStatus.SIGNED: // 736000001 - Signed
      actions.canMarkAsFollowUp = true;
      actions.canConvertToOrder = false;
      actions.canMarkAsExpired = false;
      break;

    case QuoteStatus.DRAFT: // 736000002 - Draft
      actions.canMarkAsFollowUp = true;
      break;

    default:
      // For Draft (0) or any other status
      actions.canMarkAsFollowUp = true;
      actions.canMarkAsRevised = true;
      actions.canMarkAsExpired = true;
      break;
  }

  return actions;
};

/**
 * Check if any actions are available
 */
export const hasAnyActions = (statuscode: number): boolean => {
  const actions = getAvailableActions(statuscode);
  return Object.values(actions).some((action) => action === true);
};


/**
 * Generate search query for quotes
 */
export const generateQuotesSearchQuery = (searchText: string): string => {
  return generateSearchQuery(searchText, [
    "name",
    "quotenumber",
    "dogma_projectreference",
  ]);
};


/**
 * Generate status filter query for quotes
 */
export const generateStatusFilterQuery = (statuscode: number): string => {
  return `statuscode eq ${statuscode}`;
};

/**
 * Generate region filter query for quotes
 * Filters quotes by region
 */
export const generateRegionFilterQuery = (region: number): string => {
  if (!region) return "";
  return `dogma_region eq ${region}`;
};

/**
 * Generate account filter query for quotes
 * Filters quotes by the account/customer ID
 */
export const generateAccountFilterQuery = (accountId: string): string => {
  if (!accountId) return "";
  return `_dogma_customer_value eq '${accountId}'`;
};

/**
 * Generate product filter query for quotes
 * Note: This will need to be implemented on the backend to filter quotes
 * that have line items with the specified product
 */
export const generateProductFilterQuery = (productName: string): string => {
  if (!productName) return "";
  return `dogma_name eq '${productName}'`;
};

/**
 * Generate category filter query for quotes
 * Note: This will need to be implemented on the backend to filter quotes
 * that have sub-products with the specified category
 */
export const generateCategoryFilterQuery = (categoryName: string): string => {
  if (!categoryName) return "";
  // Use category name for filtering
  return `dogma_categoryname eq '${categoryName}'`;
};

/**
 * Generate option filter query for quotes
 * Note: This will need to be implemented on the backend to filter quotes
 * that have sub-products with the specified option
 */
export const generateOptionFilterQuery = (optionName: string): string => {
  if (!optionName) return "";
  // Use option name for filtering
  return `dogma_optionname eq '${optionName}'`;
};

/**
 * Generate date range filter query for quotes
 */
export const generateDateRangeFilterQuery = (startDate: string, endDate: string): string => {
  if (!startDate && !endDate) return "";
  const parts: string[] = [];
  if (startDate) parts.push(`createdon ge ${startDate}`);
  if (endDate) parts.push(`createdon le ${endDate}`);
  return parts.join(" and ");
};

/**
 * Generate project type filter query for quotes
 */
export const generateProjectTypeFilterQuery = (projectType: number): string => {
  return `dogma_projecttype eq ${projectType}`;
};

/**
 * Combine multiple filter queries with 'and' operator
 */
export const combineFilterQueries = (filters: string[]): string => {
  const validFilters = filters.filter((f) => f && f.trim() !== "");
  return validFilters.length > 0 ? validFilters.join(" and ") : "";
};
